import { useState, useCallback, useEffect } from 'react'
import { api } from '../api/client.js'

export interface ContactCenterQueueResponse {
  id: string
  status: boolean
  name: string
  description: string | null
  serviceLevel: number
  cidNamePrefix: string | null
  strategy: string
  campaignId: string | null
  campaignName: string | null
  callRecording: boolean
  holdMusicId: string | null
  agentAnnouncementId: string | null
  agentFailedAnnouncementId: string | null
  abandonedResumePosition: boolean
  maxWaitTime: number
  maxWaitNoAgentTimeReached: number
  joinAnnouncementId: string | null
  queueExitKey: string
  timeBaseScore: string
  discardAbandonedAfter: number
  maxWaitNoAgent: number
  ringProgressivelyDelay: number
  applyTierRules: boolean
  destinationType: string | null
  destinationId: string | null
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
  createdByName: string | null
  updatedByName: string | null
}

export interface ContactCenterQueueCreateInput {
  status?: boolean
  name: string
  description?: string
  serviceLevel?: number
  cidNamePrefix?: string
  strategy: string
  campaignId?: string
  callRecording?: boolean
  holdMusicId?: string
  agentAnnouncementId?: string
  agentFailedAnnouncementId?: string
  abandonedResumePosition?: boolean
  maxWaitTime?: number
  maxWaitNoAgentTimeReached?: number
  joinAnnouncementId?: string
  queueExitKey?: string
  timeBaseScore?: string
  discardAbandonedAfter?: number
  maxWaitNoAgent?: number
  ringProgressivelyDelay?: number
  applyTierRules?: boolean
  destinationType?: string
  destinationId?: string
  createdBy?: string
}

export interface ContactCenterQueueUpdateInput {
  status?: boolean
  name?: string
  description?: string
  serviceLevel?: number
  cidNamePrefix?: string
  strategy?: string
  campaignId?: string
  callRecording?: boolean
  holdMusicId?: string
  agentAnnouncementId?: string
  agentFailedAnnouncementId?: string
  abandonedResumePosition?: boolean
  maxWaitTime?: number
  maxWaitNoAgentTimeReached?: number
  joinAnnouncementId?: string
  queueExitKey?: string
  timeBaseScore?: string
  discardAbandonedAfter?: number
  maxWaitNoAgent?: number
  ringProgressivelyDelay?: number
  applyTierRules?: boolean
  destinationType?: string
  destinationId?: string
  updatedBy?: string
}

export interface ContactCenterQueueListResponse {
  items: ContactCenterQueueResponse[]
  total: number
  limit: number
  offset: number
}

export function useContactCenterQueues() {
  const [items, setItems] = useState<ContactCenterQueueResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<{ total: number; limit: number; offset: number; totalPages: number } | null>(null)

  const fetchItems = useCallback(async (searchQuery = '', page = 1, limit = 100) => {
    setLoading(true)
    try {
      const offset = (page - 1) * limit
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''
      const response = await api.get<ContactCenterQueueListResponse>(`/admin/contact-center-queues?limit=${limit}&offset=${offset}${searchParam}`)
      setItems(response.items)
      setPagination({
        total: response.total,
        limit: response.limit,
        offset: response.offset,
        totalPages: Math.ceil(response.total / response.limit)
      })
    } catch (error) {
      console.error('Error fetching contact center queues:', error)
      setItems([])
      setPagination(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const create = useCallback(async (data: ContactCenterQueueCreateInput) => {
    setLoading(true)
    try {
      // The route replies { item }, like GET /:id — unwrap it so callers get a
      // queue (with its new `id`), not the envelope.
      const response = await api.post<{ item: ContactCenterQueueResponse }>(
        '/admin/contact-center-queues',
        data,
      )
      return response.item
    } catch (error) {
      console.error('Error creating contact center queue:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const update = useCallback(async (id: string, data: ContactCenterQueueUpdateInput) => {
    setLoading(true)
    try {
      const response = await api.put<{ item: ContactCenterQueueResponse }>(
        `/admin/contact-center-queues/${id}`,
        data,
      )
      return response.item
    } catch (error) {
      console.error('Error updating contact center queue:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const remove = useCallback(async (id: string) => {
    setLoading(true)
    try {
      const response = await api.delete<{ success: boolean }>(`/admin/contact-center-queues/${id}`)
      return response
    } catch (error) {
      console.error('Error deleting contact center queue:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const getById = useCallback(async (id: string) => {
    setLoading(true)
    try {
      const response = await api.get<{ item: ContactCenterQueueResponse }>(`/admin/contact-center-queues/${id}`)
      return response.item as ContactCenterQueueResponse
    } catch (error) {
      console.error('Error fetching contact center queue:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchItems()
  }, [fetchItems])

  return {
    items,
    loading,
    pagination,
    refetch: fetchItems,
    create,
    update,
    remove,
    getById,
  }
}
